import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { Cancel, SaveAlt } from "@mui/icons-material";
import { convertBase64 } from "../../../utils/Utility";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { pdf } from "../../../constants/applicationConstants";
import { uploadSignedAgreementById, UploadSignedAgreementInfo } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CloseIcon from "@mui/icons-material/Close";
import { FileUp } from "lucide-react";
import { useModalStyles } from "../../common/useModalStyles";

interface UploadSignedAgreementProps {
    open: boolean;
    onClose: () => void;
    practiceProductId: number | null;
}

const UploadSignedAgreementPopup: React.FC<UploadSignedAgreementProps> = ({
    open,
    onClose,
    practiceProductId,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
      tokens,
      paperSx,
      headerSx,
      iconBoxSx,
      closeIconSx,
      bodySx,
      footerSx,
      primaryBtnSx,
      cancelBtnSx,
    } = useModalStyles();
    const { control, handleSubmit,getValues, reset, setValue, watch } = useForm<UploadSignedAgreementInfo>({});
    const dispatch = useAppDispatch();

    useEffect(() => {
        reset();
        practiceProductId && setValue('practiceProductId', practiceProductId);
    }, [open])

    const onSubmit = async (data: UploadSignedAgreementInfo) => {
        const response = await dispatch(uploadSignedAgreementById(data));
        if (response.meta.requestStatus === 'fulfilled') {
            let successObj: SingleAlertInfo = {
                message: "Uploading Signed Agreement is successful.",
                alertType: "success"
            }
            dispatch(setSingleAlertObj(successObj));
            onClosePopup();
        };
    };

    const handleDropzoneChange = async (uploadedFiles: ExtendedFile[], fieldName: any) => {
        if (uploadedFiles && uploadedFiles.length > 0) {
            const base64File = String(await convertBase64(uploadedFiles[0]));
            const fileName = uploadedFiles[0].name;
            const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
            const base64Substring = base64File.substring("data:application/pdf;base64,".length, base64File.length);
            setValue('content', base64Substring);
            setValue('documentName', fileName);
            setValue('fileType', fileExtension);
            setValue('type', "SERVICEAGREEMENT")
        }
    };

    const handleDeleteFile = () => {
        setValue('content', "");
        setValue('documentName', "");
        setValue('fileType', "");
        setValue('type', "");
    };

    
  const onClosePopup = () => {
    reset();
    onClose();
  }

    return (
        <>
            <Dialog
                open={open}
                keepMounted
                onClose={onClosePopup}
                aria-describedby="upload-signed-agreement-dialog-description"
                onClick={(event) => {
                    event.stopPropagation();
                }}
                fullWidth
                maxWidth="md"
                fullScreen={isMobile}
                scroll="paper"
                PaperProps={{ sx: paperSx(isMobile) }}
            >
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(tokens.accent)}>
                            <FileUp size={20} color={tokens.accent} strokeWidth={1.8} />
                        </Box>
                        <Box sx={{ flex: 1, pr: 4 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                                Upload Signed Agreement
                            </Typography>
                            <Typography id="upload-signed-agreement-dialog-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                                Attach the signed agreement document in PDF format.
                            </Typography>
                        </Box>
                        <IconButton onClick={onClosePopup} size="small" sx={closeIconSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            ...bodySx,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            scrollbarGutter: "stable",
                            "&::-webkit-scrollbar": { width: 6, height: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Dropzone
                                    name="content"
                                    onFileChange={() => { }}
                                    onFileChangeTest={handleDropzoneChange}
                                    onDeleteFile={handleDeleteFile}
                                    control={control}
                                    maxFileCount={1}
                                    allowedFileTypes={pdf}
                                    label="Attach Signed Agreement Document"
                                    rules={{required: "Signed Agreement Document is required."}}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={footerSx}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Box
                                component="button"
                                type="submit"
                                sx={{
                                    ...primaryBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#fff",
                                    order: { xs: 1, sm: 2 },
                                }}
                            >
                                <SaveAlt sx={{ fontSize: "1.1rem" }} />
                                Upload
                            </Box>
                            <Box
                                component="button"
                                type="button"
                                onClick={onClose}
                                sx={{
                                    ...cancelBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "1px solid",
                                    cursor: "pointer",
                                    order: { xs: 2, sm: 1 },
                                }}
                            >
                                <Cancel sx={{ fontSize: "1.1rem" }} />
                                Cancel
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default UploadSignedAgreementPopup;
